// apps/web/src/components/UnifiedInitiativeSection.tsx

"use client"

import Link from "next/link"
import { useEffect, useState } from "react"
import { Card, CardContent } from "@/components/ui/card"
import { buildCO2ComparisonView, type CO2Comparison } from "@/lib/co2-impact"
import { sdgIconsForInitiative } from "@/lib/sdg-catalogue"
import type { ForestImpact } from "@/lib/settings"
import { SDGIcon } from "./SDGIcon"

interface UnifiedInitiativeSectionProps {
  forest: ForestImpact
  co2Comparisons: CO2Comparison[]
  variant?: "default" | "minimal"
  initialRandomIndex?: number
}

const relevantSDGs = sdgIconsForInitiative()

export function UnifiedInitiativeSection({
  forest,
  co2Comparisons,
  variant = "default",
  initialRandomIndex = 0,
}: UnifiedInitiativeSectionProps) {
  const [currentIndex, setCurrentIndex] = useState(
    co2Comparisons.length > 0 && initialRandomIndex < co2Comparisons.length ? initialRandomIndex : 0
  )
  const [opacity, setOpacity] = useState(1)
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false)

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)")
    const updatePreference = () => setPrefersReducedMotion(mediaQuery.matches)
    updatePreference()
    mediaQuery.addEventListener("change", updatePreference)
    return () => mediaQuery.removeEventListener("change", updatePreference)
  }, [])

  useEffect(() => {
    if (prefersReducedMotion || co2Comparisons.length <= 1) return

    const interval = setInterval(() => {
      setOpacity(0.7)
      setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % co2Comparisons.length)
        setOpacity(1)
      }, 400)
    }, 5000)

    return () => clearInterval(interval)
  }, [co2Comparisons.length, prefersReducedMotion])

  const currentComparison = co2Comparisons.length > 0 ? co2Comparisons[currentIndex] : null
  const comparisonView =
    currentComparison && forest.co2CapturedKg > 0
      ? buildCO2ComparisonView(forest, currentComparison)
      : null

  if (variant === "minimal") {
    return (
      <div className="text-center">
        <p className="text-sm text-zinc-800">
          Through our {forest.forestName}, we've planted{" "}
          <span className="font-semibold">{forest.numberOfTrees.toLocaleString()} trees</span>{" "}
          capturing{" "}
          <span className="font-semibold">
            {forest.co2CapturedTonnes.toLocaleString()} tonnes of CO₂
          </span>
          .
          {comparisonView?.isComplete && (
            <>
              <br />
              That's about{" "}
              <Link
                href="/co2-calculations"
                className="underline text-zinc-800 hover:text-[hsl(var(--giof-pink))] transition-colors"
                style={
                  prefersReducedMotion
                    ? undefined
                    : {
                        opacity,
                        transition: "opacity 400ms ease-in-out",
                      }
                }
              >
                {comparisonView.shortDescription}
              </Link>
              .
            </>
          )}
        </p>
        <p className="text-xs text-zinc-600 mt-2">
          Supporting UN Sustainable Development Goals (
          <Link
            href="/sdg"
            className="underline text-zinc-600 hover:text-[hsl(var(--giof-pink))] transition-colors"
          >
            SDG
          </Link>
          ) through digital innovation and environmental action.
        </p>
      </div>
    )
  }

  return (
    <Card className="bg-brand-90 border-2 border-brand-80 max-w-6xl mx-auto">
      <CardContent className="p-8 text-center">
        <div className="text-brand-1 text-sm mb-6">
          <p>
            Through our {forest.forestName}, we've planted{" "}
            <span className="text-brand-1 font-medium">
              {forest.numberOfTrees.toLocaleString()} trees
            </span>{" "}
            capturing{" "}
            <span className="text-brand-1 font-medium">
              {forest.co2CapturedTonnes.toLocaleString()} tonnes of CO₂.
            </span>
          </p>
          {comparisonView?.isComplete && (
            <p className="mt-2">
              That's about{" "}
              <Link
                href="/co2-calculations"
                className="text-brand-1 hover:text-[hsl(var(--giof-pink))] underline underline-offset-2 transition-colors"
                style={
                  prefersReducedMotion
                    ? undefined
                    : {
                        opacity,
                        transition: "opacity 400ms ease-in-out",
                      }
                }
              >
                {comparisonView.shortDescription}
              </Link>
              .
            </p>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 mb-4 px-4 max-w-sm sm:max-w-none mx-auto">
          {relevantSDGs.map((sdg) => (
            <div key={sdg.number} className="shrink-0">
              <SDGIcon
                number={sdg.number}
                title={sdg.title}
                iconUrl={sdg.iconUrl}
                size={64}
                className="w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20"
              />
            </div>
          ))}
        </div>

        <p className="text-brand-1/80 text-xs">
          Supporting UN Sustainable Development Goals through digital innovation and environmental
          action.{" "}
          <Link
            href="/sdg"
            className="text-brand-1 hover:text-[hsl(var(--giof-pink))] underline underline-offset-2 transition-colors"
          >
            Learn more
          </Link>
        </p>
      </CardContent>
    </Card>
  )
}
